import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, CalendarRange } from "lucide-react";
import { api, coachHeaders } from "../lib/api";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const now = new Date();
const debutSaison = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
const SAISONS = [0, 1, 2].map((i) => debutSaison - i).map((y) => ({ label: `Saison ${y}-${y + 1}`, du: `${y}-08-01`, au: `${y + 1}-07-31` }));
const MOIS = { label: "Ce mois-ci", du: ymd(new Date(now.getFullYear(), now.getMonth(), 1)), au: ymd(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
const PRESETS = [...SAISONS, MOIS];
const TYPES = [["entrainement", "Entraînements"], ["match", "Matchs"], ["tournoi", "Tournois"], ["total", "Total"]];
const fr = (iso) => iso.split("-").reverse().join("/");

const exportExcel = async (data) => {
  const XLSX = await import("xlsx");
  const head = ["Nom", "Prénom", ...TYPES.flatMap(([, l]) => [`${l} présent`, `${l} absent`, `${l} sans réponse`])];
  const rows = (data?.joueurs ?? []).map((j) => [j.nom.toUpperCase(), j.prenom, ...TYPES.flatMap(([k]) => [j[k]?.present ?? 0, j[k]?.absent ?? 0, j[k]?.sans_reponse ?? 0])]);
  const c = data?.convocations ?? {};
  const ws = XLSX.utils.aoa_to_sheet([
    [`Présences USC Jemappes U12 C du ${fr(data.du)} au ${fr(data.au)}`],
    [`Convocations : ${c.entrainement ?? 0} entraînement(s), ${c.match ?? 0} match(s), ${c.tournoi ?? 0} tournoi(s)`],
    [], head, ...rows, [], ["Document confidentiel – usage interne au club."],
  ]);
  ws["!cols"] = [18, 14, ...Array(12).fill(11)].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Présences");
  XLSX.writeFile(wb, `presences-${data.du}-au-${data.au}.xlsx`);
};

const Cell = ({ s, testid }) => (
  <td data-testid={testid} className="whitespace-nowrap px-2 py-2 text-center">
    <span className="font-semibold text-lime">{s?.present ?? 0}</span><span className="text-dim"> / </span><span className="text-signal">{s?.absent ?? 0}</span>
    {(s?.sans_reponse ?? 0) > 0 && <span className="ml-1 text-xs text-dim">({s.sans_reponse} ?)</span>}
  </td>
);

export const SeasonPresence = ({ code }) => {
  const [du, setDu] = useState(SAISONS[0].du);
  const [au, setAu] = useState(SAISONS[0].au);
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    if (!du || !au) return;
    api.get("/presences", { ...coachHeaders(code), params: { du, au } }).then((r) => setData(r.data ?? null)).catch(() => setData(null));
  }, [code, du, au]);
  useEffect(() => { load(); }, [load]);
  const joueurs = Array.isArray(data?.joueurs) ? data.joueurs : [];
  const c = data?.convocations ?? {};
  const inp = "rounded-lg border border-line bg-ink px-2 py-1.5 text-sm text-chalk outline-none focus:border-lime [color-scheme:dark]";
  return (
    <div data-testid="season-presence" className="rounded-2xl border border-line bg-pitch p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl uppercase text-chalk">Fiche présences</h2>
        <button data-testid="presence-export-excel" onClick={() => (joueurs.length ? exportExcel(data).catch(() => toast.error("Export impossible")) : toast.error("Aucun joueur"))}
          className="inline-flex items-center gap-1 rounded-full bg-lime px-4 py-2 text-sm font-semibold text-ink"><FileSpreadsheet className="h-4 w-4" />Exporter en Excel</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button key={p.label} data-testid={`presence-preset-${p.du}-${p.au}`} onClick={() => { setDu(p.du); setAu(p.au); }}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${du === p.du && au === p.au ? "border-lime bg-lime text-ink" : "border-line text-chalk hover:border-lime"}`}>{p.label}</button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-chalk">
        <CalendarRange className="h-4 w-4 text-lime" />Du
        <input data-testid="presence-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} className={inp} />
        au
        <input data-testid="presence-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} className={inp} />
      </div>
      <p data-testid="presence-convocations" className="mt-2 text-sm text-dim">
        {c.entrainement ?? 0} entraînement(s) · {c.match ?? 0} match(s) · {c.tournoi ?? 0} tournoi(s) sur la période (convocations en cours et archivées, selon la date du match).
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead>
            <tr className="font-mono text-[11px] uppercase tracking-[0.15em] text-dim">
              <th className="px-2 py-2">Joueur</th>
              {TYPES.map(([k, l]) => <th key={k} className="px-2 py-2 text-center">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {joueurs.length === 0 && <tr><td colSpan={5} className="px-2 py-4 text-dim">Aucun joueur dans l'effectif.</td></tr>}
            {joueurs.map((j) => (
              <tr key={j.id} data-testid="presence-row" className="border-t border-line">
                <td className="px-2 py-2 font-semibold text-chalk">{j.prenom} {j.nom.toUpperCase()}</td>
                {TYPES.map(([k]) => <Cell key={k} s={j[k]} testid={`presence-${k}-${j.id}`} />)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-dim"><span className="text-lime">présent</span> / <span className="text-signal">absent</span> · (n ?) = convoqué sans réponse</p>
    </div>
  );
};
